type PendingWrite = { key: string; attempts: number; updatedAt: string };
const pending = new Map<string, Promise<void>>();
const STORAGE_KEY = "momentum-peaks:pending-writes";
const REVISION_KEY = "momentum-peaks:resource-revisions";

function mark(key: string, attempts: number): void {
  if (typeof localStorage === "undefined") return;
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]") as unknown;
    const current = Array.isArray(parsed) ? parsed as PendingWrite[] : [];
    const next = current.filter((entry) => entry.key !== key);
    if (attempts >= 0) next.push({ key, attempts, updatedAt: new Date().toISOString() });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch { /* storage unavailable or corrupt */ }
}

const wait = (milliseconds: number) => new Promise<void>((resolve) => setTimeout(resolve, milliseconds));

/** Serializes writes per resource and retries transient/offline failures. */
export function reliableWrite(key: string, operation: () => Promise<void>, maxAttempts = 4): Promise<void> {
  const previous = pending.get(key) ?? Promise.resolve();
  const run = previous.catch(() => undefined).then(async () => {
    let lastError: unknown;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      mark(key, attempt);
      try {
        if (typeof navigator !== "undefined" && !navigator.onLine) throw new Error("Offline");
        await operation();
        mark(key, -1);
        return;
      } catch (error) {
        lastError = error;
        if (attempt < maxAttempts) await wait(250 * 2 ** (attempt - 1));
      }
    }
    throw lastError;
  }).finally(() => {
    if (pending.get(key) === run) pending.delete(key);
  });
  pending.set(key, run);
  return run;
}

export function getPendingWriteKeys(): string[] {
  if (typeof localStorage === "undefined") return [];
  try { return (JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]") as PendingWrite[]).map((entry) => entry.key); }
  catch { return []; }
}

/** Produces a monotonic, persisted revision even when several tabs save quickly. */
export function nextClientRevision(key: string, observed = 0): number {
  const timeRevision = Date.now();
  if (typeof localStorage === "undefined") return Math.max(observed + 1, timeRevision);
  try {
    const revisions = JSON.parse(localStorage.getItem(REVISION_KEY) ?? "{}") as Record<string, number>;
    const next = Math.max(observed + 1, (revisions[key] ?? 0) + 1, timeRevision);
    revisions[key] = next;
    localStorage.setItem(REVISION_KEY, JSON.stringify(revisions));
    return next;
  } catch {
    return Math.max(observed + 1, timeRevision);
  }
}

export function rememberClientRevision(key: string, revision: number): void {
  if (typeof localStorage === "undefined" || !Number.isFinite(revision)) return;
  try {
    const revisions = JSON.parse(localStorage.getItem(REVISION_KEY) ?? "{}") as Record<string, number>;
    revisions[key] = Math.max(revisions[key] ?? 0, revision);
    localStorage.setItem(REVISION_KEY, JSON.stringify(revisions));
  } catch { /* storage unavailable */ }
}
